import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function ContractTestRender() {
  const token = localStorage.getItem('token');

  // Form state
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [selectedDealId, setSelectedDealId] = useState('');

  // Data loading
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(true);

  // Render state
  const [generating, setGenerating] = useState(false);
  const [renderError, setRenderError] = useState('');
  const [output, setOutput] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Load deals on mount
  useEffect(() => {
    const loadDeals = async () => {
      try {
        setLoadingDeals(true);
        const res = await axios.get(`${API_URL}/deals`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setDeals(res.data || []);
      } catch (err) {
        console.error('Failed to load deals:', err);
        setRenderError('Failed to load deals. Check network and API.');
      } finally {
        setLoadingDeals(false);
      }
    };

    loadDeals();
  }, [token]);

  // Handle generate button
  const handleGenerate = async () => {
    if (!templateVersionId || !selectedDealId) {
      setRenderError('Please enter template version ID and select a deal');
      return;
    }

    try {
      setGenerating(true);
      setRenderError('');
      setOutput(null);

      const response = await axios.post(
        `${API_URL}/contracts/render`,
        {
          template_version_id: parseInt(templateVersionId),
          deal_id: parseInt(selectedDealId),
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setOutput(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to render contract';
      setRenderError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  // Handle download button
  const handleDownload = async () => {
    if (!output || !output.download_url) {
      setRenderError('No output available to download');
      return;
    }

    try {
      setDownloadLoading(true);

      const response = await axios.get(
        `${API_URL}${output.download_url}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob',
        }
      );

      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = output.filename || 'contract.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to download file';
      setRenderError(errorMsg);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Styles
  const containerStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: SPACING.XL,
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
  };

  const cardStyle = {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.LG,
    marginBottom: SPACING.LG,
    boxShadow: SHADOWS.LIGHT,
  };

  const labelStyle = {
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_PRIMARY,
    marginBottom: SPACING.SM,
    display: 'block',
  };

  const inputStyle = {
    width: '100%',
    padding: SPACING.MD,
    border: `1px solid #ddd`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY,
    marginBottom: SPACING.MD,
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
  };

  const buttonStyle = {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: '700',
    cursor: generating || !templateVersionId || !selectedDealId ? 'not-allowed' : 'pointer',
    opacity: generating || !templateVersionId || !selectedDealId ? 0.6 : 1,
    transition: 'all 0.2s ease',
  };

  const errorStyle = {
    backgroundColor: '#ffe6e6',
    color: '#c00',
    padding: SPACING.MD,
    borderRadius: RADIUS.DEFAULT,
    marginBottom: SPACING.LG,
    border: `1px solid #ffcccc`,
    ...TYPOGRAPHY.BODY_SM,
  };

  const successMessageStyle = {
    backgroundColor: '#e6ffe6',
    color: '#060',
    padding: SPACING.MD,
    borderRadius: RADIUS.DEFAULT,
    marginBottom: SPACING.LG,
    border: `1px solid #ccffcc`,
    ...TYPOGRAPHY.BODY_SM,
  };

  const outputCardStyle = {
    ...cardStyle,
    borderLeft: `4px solid ${COLORS.PRIMARY}`,
  };

  const metadataStyle = {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_SECONDARY,
    marginBottom: SPACING.SM,
  };

  const downloadButtonStyle = {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: '700',
    cursor: downloadLoading ? 'not-allowed' : 'pointer',
    opacity: downloadLoading ? 0.6 : 1,
    transition: 'all 0.2s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ marginBottom: SPACING.XL }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING.MD }}>
          <Link
            to="/dashboard"
            style={{
              color: COLORS.LINK,
              textDecoration: 'none',
              marginRight: SPACING.MD,
              ...TYPOGRAPHY.BODY,
            }}
          >
            ← Back
          </Link>
        </div>
        <h1 style={TYPOGRAPHY.H1}>Test: Generate Contract</h1>
        <p style={{ ...TYPOGRAPHY.BODY, color: COLORS.TEXT_SECONDARY, marginTop: SPACING.SM }}>
          Test the contract render flow. Select a template version and deal, then generate a DOCX contract.
        </p>
      </div>

      {/* Error loading deals */}
      {renderError && !output && (
        <div style={errorStyle}>
          <strong>Error:</strong> {renderError}
        </div>
      )}

      {/* Form */}
      <div style={cardStyle}>
        <h2 style={{ ...TYPOGRAPHY.H3, marginBottom: SPACING.LG }}>Render Options</h2>

        <div>
          <label style={labelStyle}>Template Version ID</label>
          <input
            type="number"
            style={inputStyle}
            value={templateVersionId}
            onChange={(e) => setTemplateVersionId(e.target.value)}
            placeholder="e.g. 1"
            disabled={generating}
          />
          <p style={{ ...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_LIGHT, marginTop: `-${SPACING.MD}`, marginBottom: SPACING.MD }}>
            Enter the ID of the template version to render
          </p>
        </div>

        <div>
          <label style={labelStyle}>Deal</label>
          {loadingDeals ? (
            <p style={TYPOGRAPHY.BODY_SM}>Loading deals...</p>
          ) : (
            <select
              style={selectStyle}
              value={selectedDealId}
              onChange={(e) => setSelectedDealId(e.target.value)}
              disabled={generating}
            >
              <option value="">-- Select a deal --</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>
                  Deal {deal.id} - {deal.merchant?.legal_name || 'Unknown'} ({deal.state})
                </option>
              ))}
            </select>
          )}
        </div>

        <button
          style={buttonStyle}
          onClick={handleGenerate}
          disabled={generating || loadingDeals || !templateVersionId || !selectedDealId}
        >
          {generating ? 'Generating...' : 'Generate Contract'}
        </button>
      </div>

      {/* Render error */}
      {renderError && output && (
        <div style={errorStyle}>
          <strong>Error:</strong> {renderError}
        </div>
      )}

      {/* Success output */}
      {output && (
        <div style={outputCardStyle}>
          <div style={successMessageStyle}>
            ✓ Contract generated successfully
          </div>

          <h3 style={{ ...TYPOGRAPHY.H4, marginBottom: SPACING.MD }}>Generated Output</h3>

          <div style={{ marginBottom: SPACING.MD }}>
            <p style={metadataStyle}>
              <strong>Filename:</strong> {output.filename}
            </p>
            <p style={metadataStyle}>
              <strong>Size:</strong> {(output.file_size_bytes / 1024).toFixed(2)} KB
            </p>
            <p style={metadataStyle}>
              <strong>Generated:</strong> {new Date(output.generated_at).toLocaleString()}
            </p>
            <p style={metadataStyle}>
              <strong>Template Version:</strong> {output.template_version_id}
            </p>
            <p style={metadataStyle}>
              <strong>Deal ID:</strong> {output.deal_id}
            </p>
          </div>

          <button
            style={downloadButtonStyle}
            onClick={handleDownload}
            disabled={downloadLoading}
          >
            {downloadLoading ? 'Downloading...' : 'Download DOCX'}
          </button>
        </div>
      )}
    </div>
  );
}
